import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Flex } from "uikit-react";
import { Link } from "react-router-dom";
import { useCookies } from "react-cookie";
import RecordsPerPage from "../../../common/RecordsPerPage";
import Pagination from "../../../common/Pagination";
import StorageLocationAdd from "./add";

const DEFAULT_RPP = 10;

const LOCATION_TYPE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Warehouse", value: "warehouse" },
  { label: "Property Closet", value: "property_closet" },
  { label: "Linen Room", value: "linen_room" },
  { label: "Garage / Shed", value: "garage" },
  { label: "Other", value: "other" },
];

const STATUS_FILTER_OPTIONS = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const USAGE_OPTIONS = [
  { label: "Housekeeping", value: "housekeeping" },
  { label: "Maintenance", value: "maintenance" },
  { label: "Owner Supplies", value: "owner_supplies" },
  { label: "Guest Amenities", value: "guest_amenities" },
];

const LOCATION_DUMMY = [
  {
    id: "loc-1",
    name: "Main Warehouse",
    code: "WH1",
    location_type: "warehouse",
    property_name: "",
    address: "123 Coastal Way, Cannon Beach",
    usage: ["housekeeping", "maintenance"],
    notes: "Bulk storage for linens, paper goods, and cleaning supplies.",
    active: true,
  },
  {
    id: "loc-2",
    name: "Driftwood Villa – Owner Closet",
    code: "DW-OC",
    location_type: "property_closet",
    property_name: "101 Driftwood Villa",
    address: "",
    usage: ["owner_supplies"],
    notes: "Locked owner closet; do not use for guest supplies.",
    active: true,
  },
  {
    id: "loc-3",
    name: "Sand Dollar Linen Room",
    code: "SD-LINEN",
    location_type: "linen_room",
    property_name: "112 Sand Dollar Suite",
    address: "",
    usage: ["housekeeping"],
    notes: "Sheets and towels for Sand Dollar + nearby units.",
    active: true,
  },
  {
    id: "loc-4",
    name: "Maintenance Garage",
    code: "GAR1",
    location_type: "garage",
    property_name: "",
    address: "Behind office, roll-up door #2",
    usage: ["maintenance"],
    notes: "Tools, filters, light bulbs, small parts.",
    active: false,
  },
];

const typeLabel = (value) =>
  (LOCATION_TYPE_OPTIONS.find((t) => t.value === value) || {}).label ||
  value ||
  "—";

const usageLabels = (values = []) => {
  if (!values.length) return "—";
  return values
    .map(
      (v) =>
        (USAGE_OPTIONS.find((u) => u.value === v) || {}).label || v
    )
    .join(", ");
};

export default function StorageLocationsIndex() {
  const [cookies] = useCookies(["records-per-page"]);
  const recordsPerPage =
    Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  const [locations, setLocations] = useState(LOCATION_DUMMY);

  const [criteria, setCriteria] = useState({
    search: "",
    type: "",
    status: "",
    page: 0,
  });

  useEffect(() => {
    document.title = "Storage Locations - RueBaRue";
  }, []);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
      page:
        key === "search" || key === "type" || key === "status"
          ? 0
          : prev.page,
    }));
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((prev) => ({ ...prev, page: newPageIndex }));
  }, []);

  const toggleActive = useCallback((locId) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locId ? { ...loc, active: !loc.active } : loc
      )
    );
  }, []);

  const filtered = useMemo(() => {
    return locations.filter((loc) => {
      if (criteria.type && loc.location_type !== criteria.type) {
        return false;
      }

      if (criteria.status === "active" && !loc.active) return false;
      if (criteria.status === "inactive" && loc.active) return false;

      if (criteria.search) {
        const q = criteria.search.toLowerCase();
        const hay = `${loc.name} ${loc.code || ""} ${loc.property_name || ""} ${
          loc.address || ""
        }`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [locations, criteria]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / recordsPerPage)
  );
  const pageLocations = filtered.slice(
    criteria.page * recordsPerPage,
    criteria.page * recordsPerPage + recordsPerPage
  );

  return (
    <>
      <section>
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Storage Locations</h1>
        </div>

        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <button
              className="uk-button hms-btn hms-red-btn"
              uk-toggle="target: #modal-storage-location-add"
            >
              <span uk-icon="icon: plus; ratio: 0.7"></span> Location
            </button>
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              {/* Filters */}
              <Flex
                alignment="between top"
                className="guest-form-top-filters"
              >
                <form
                  className="uk-form uk-flex uk-flex-middle hms-form"
                  onSubmit={(e) => e.preventDefault()}
                >
                  {/* Search */}
                  <div className="uk-inline uk-margin-small-right">
                    <div className="uk-search uk-search-default uk-form-custom">
                      {criteria.search ? (
                        <a
                          className="uk-form-icon-flip uk-form-icon"
                          uk-icon="icon: close"
                          onClick={() =>
                            handleCriteriaChange("search", "")
                          }
                        ></a>
                      ) : (
                        <span
                          className="uk-search-icon-flip"
                          uk-search-icon="true"
                        />
                      )}
                      <input
                        className="uk-search-input"
                        type="search"
                        placeholder="Search location"
                        value={criteria.search}
                        onChange={(e) =>
                          handleCriteriaChange("search", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {/* Type filter */}
                  <div
                    className="uk-inline uk-margin-small-right"
                    uk-form-custom="target: > * > span:first-child"
                  >
                    <select
                      value={criteria.type}
                      onChange={(e) =>
                        handleCriteriaChange("type", e.target.value)
                      }
                    >
                      {LOCATION_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className="uk-button uk-button-default"
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {criteria.type
                          ? typeLabel(criteria.type)
                          : "All Types"}
                      </span>
                      <span
                        uk-icon="icon: chevron-down"
                        className="hms-align-right"
                      ></span>
                    </button>
                  </div>

                  {/* Status filter */}
                  <div
                    className="uk-inline uk-margin-small-right"
                    uk-form-custom="target: > * > span:first-child"
                  >
                    <select
                      value={criteria.status}
                      onChange={(e) =>
                        handleCriteriaChange("status", e.target.value)
                      }
                    >
                      {STATUS_FILTER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className="uk-button uk-button-default"
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {criteria.status
                          ? STATUS_FILTER_OPTIONS.find(
                              (o) => o.value === criteria.status
                            )?.label
                          : "All Statuses"}
                      </span>
                      <span
                        uk-icon="icon: chevron-down"
                        className="hms-align-right"
                      ></span>
                    </button>
                  </div>
                </form>
              </Flex>

              {/* Table */}
              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th>Active</th>
                    <th className="uk-table-expand">Location</th>
                    <th>Type</th>
                    <th>Property</th>
                    <th>Usage</th>
                    <th>Notes</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageLocations.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <div className="uk-text-center uk-padding-small">
                          No storage locations found. Adjust your filters.
                        </div>
                      </td>
                    </tr>
                  )}

                  {pageLocations.map((loc) => (
                    <tr key={loc.id}>
                      <td>
                        <div className="autosend-checkbox">
                          <label className="setting-switch">
                            <input
                              type="checkbox"
                              checked={!!loc.active}
                              onChange={() => toggleActive(loc.id)}
                            />
                            <span className="setting-slider setting-round"></span>
                          </label>
                        </div>
                      </td>

                      <td className="semi-bold-font">
                        {loc.name}
                      </td>

                      <td>{typeLabel(loc.location_type)}</td>
                      <td>{loc.property_name || "—"}</td>
                      <td>{usageLabels(loc.usage)}</td>
                      <td>
                        {loc.notes ? (
                          <span>
                            {loc.notes}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td className="hms-action-btn">
                        <div className="uk-inline">
                          <Link
                            to={{
                              pathname: "/operations/storage/locations/add",
                              state: { location: loc },
                            }}
                            className="uk-button"
                            uk-tooltip="Edit"
                          >
                            <img src="/images/edit.svg" alt="Edit" />
                          </Link>
                        </div>
                        <div className="uk-inline">
                          <button
                            className="uk-button"
                            uk-toggle="target: #modal-contact-delete"
                            uk-tooltip="Delete"
                          >
                            <img src="/images/delete.svg" />
                          </button>
                        </div>                      
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Flex
                alignment="between top"
                className="guest-form-top-filters"
              >
                <Pagination
                  count={totalPages}
                  index={Math.min(criteria.page, totalPages - 1)}
                  onIndexChanged={handleIndexChanged}
                />
                <RecordsPerPage />
              </Flex>
            </div>
          </div>
        </div>
      </section>
      <StorageLocationAdd />
    </>
  );
}